import { EventEmitter, h, Component, Prop, Element, Event, Method, Watch, Listen, State } from '@stencil/core';

import { FloatingUIOptions, FloatingUIPlacement } from '../../../services/interfaces';
import { FloatingUI } from '../../../services/libraries';
import { blockScroll, hasSlotContent } from '../../../services/utils';

let popoverId = 0;
const popoverOverlay = Object.assign(document.createElement('div'), { className: 'pk-popover-overlay' });

@Component({
  tag: 'pk-popover',
  styleUrl: './scss/pk-popover.scss',
  shadow: true,
})
export class PkPopover {
  // Own Properties
  // ====================

  private id = `popover-${++popoverId}`;
  private panelId = `bq-dropdown-panel-${this.id}`;
  private timeoutId: NodeJS.Timeout;

  private trigger: HTMLElement;
  private panel: HTMLDivElement;
  private arrowElement: HTMLElement;
  private floatingUI: FloatingUI;
  private headerElement: HTMLElement;
  private leaveTimer: NodeJS.Timeout;
  private enterTimer: NodeJS.Timeout;

  // Reference to host HTML element
  // ===================================

  @Element() el!: HTMLPkPopoverElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  @State() hasHeader: boolean = true;

  // Public Property API
  // ========================

  /** If `true`, the popover panel will close automatically after the `dissmisTime` being set (4 seconds default). */
  @Prop({ reflect: true }) autodismiss?: boolean = false;

  /** If `true` the trigger element won't open the popover panel on click, must be handled on integration then. */
  @Prop() disableTriggerClick?: boolean = false;

  /** If `true`, the popover panel won't close when clicking outside of it. */
  @Prop() disableOutsideClick?: boolean = false;

  /** It specifies the time in seconds after which the popover will automatically close. */
  @Prop({ reflect: true }) dismissTime?: number = 4;

  /** The distance in pixels from which to offset the panel away from its trigger. */
  @Prop({ reflect: true }) distance?: number = 20;

  /** If true, an overlay will be shown behind the popover, blocking the page content */
  @Prop({ reflect: true }) enableOverlay?: boolean = false;

  /** If `true`, the arrow element won't be shown when the panel drops. */
  @Prop({ reflect: true }) hideArrow?: boolean = false;

  /** It specifies the size of the arrow element (default is the same as `distance`). */
  @Prop({ reflect: true }) arrowSize?: number = this.distance;

  /** If `true`, the close button at the top right won't be displayed. */
  @Prop({ reflect: true }) noCloseButton?: boolean = false;

  /** Indicates whether or not the popover is open. You can use this in lieu of the show/hide methods. */
  @Prop({ reflect: true, mutable: true }) open? = false;

  /**
   * The preferred placement of the popover panel. Note that the actual placement may vary as needed to keep the panel
   * inside of the viewport.
   */
  @Prop({ reflect: true }) placement?: FloatingUIPlacement = 'bottom';

  /** It will set when the popover will be shown, on hover or click (default)  */
  @Prop({ reflect: true }) showOn?: 'click' | 'hover' = 'click';

  /** The distance in pixels from which to offset the panel along its trigger. */
  @Prop({ reflect: true }) skidding? = 0;

  /** It specifies the size of the popover panel. */
  @Prop({ reflect: true }) size?: 'small' | 'medium' | 'large' = 'medium';

  /** It specifies the outline style to apply to the popover panel. */
  @Prop({ reflect: true }) stroke?: 'none' | 'shadow' | 'stroke' = 'stroke';

  /** The delay before the hover events are triggered */
  @Prop() hoverDelay = 200;

  // Prop lifecycle events
  // =======================

  @Watch('open')
  async handleOpenChange() {
    // Since `open` property is mutable and it can trigger a re-render
    // we make sure heare to clear all timeouts ID before changing its value.
    clearTimeout(this.timeoutId);

    if (!this.open) {
      await this.hide();
      return;
    }

    await this.show();
  }

  @Watch('open')
  handleWindowScroll() {
    blockScroll(this.open && this.enableOverlay, 'popover');
  }

  @Watch('placement')
  @Watch('hideArrow')
  @Watch('arrowSize')
  @Watch('distance')
  @Watch('skidding')
  handleFloatingUIOptionsChange() {
    this.floatingUI?.init(this.floatingUIOptions);
  }

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  /** Callback handler to be called when the popover opens. Calling `event.preventDefault()` will prevent it from being opened. */
  @Event() pkShow: EventEmitter;

  /** Callback handler to be called after the popover opens and all transitions are complete. */
  @Event() pkAfterShow: EventEmitter;

  /** Callback handler to be called when the popover closes. Calling `event.preventDefault()` will prevent it from being closed. */
  @Event() pkHide: EventEmitter;

  /** Callback handler to be called after the popover closes and all transitions are complete. */
  @Event() pkAfterHide: EventEmitter;

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  async componentDidLoad() {
    this.floatingUI = new FloatingUI(this.trigger, this.panel, this.floatingUIOptions);

    // Handle header display (if empty or not)
    this.onHeaderSlotChange();

    // Show on init if open
    if (this.open) {
      await this.show();
    }

    this.handleWindowScroll();

    this.panel.addEventListener('transitionstart', this.onPanelTransitionStart, false);
    this.panel.addEventListener('transitionend', this.onPanelTransitionEnd, false);
  }

  disconnectedCallback() {
    this.panel?.removeEventListener('transitionstart', this.onPanelTransitionStart, false);
    this.panel?.removeEventListener('transitionend', this.onPanelTransitionEnd, false);
    this.floatingUI?.destroy();
    // Make sure that the overlay element has been removed from the DOM
    this.hideOverlay();
  }

  // Listeners
  // ==============

  @Listen('mousedown', { target: 'document' })
  async handleClickOutside(event: MouseEvent) {
    if (!this.open || this.disableOutsideClick) return;

    const path = event.composedPath();
    if (path.includes(this.el)) return;
    // Close when clicking outside of the close element
    await this.hide();
  }

  @Listen('mouseenter')
  async handleMouseEnter() {
    if (this.showOn !== 'hover') return;

    clearTimeout(this.leaveTimer);
    this.enterTimer = setTimeout(() => {
      this.show();
    }, this.hoverDelay);
  }

  @Listen('mouseleave')
  async handleMouseLeave() {
    if (this.showOn !== 'hover') return;

    clearTimeout(this.enterTimer);
    this.leaveTimer = setTimeout(() => {
      this.hide();
    }, this.hoverDelay);
  }

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  /** Shows the popover panel */
  @Method()
  async show() {
    this.open = true;

    this.displayOverlay();

    requestAnimationFrame(() => {
      this.showPanel();
    });
  }

  /** Hides the popover panel */
  @Method()
  async hide() {
    this.open = false;

    this.hidePanel();
    this.hideOverlay();
  }

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  private get floatingUIOptions(): FloatingUIOptions {
    return {
      placement: this.placement,
      distance: !this.hideArrow ? this.distance : this.distance - 10,
      arrow: !this.hideArrow ? this.arrowElement : null,
      arrowSize: this.arrowSize,
      sameWidth: false,
      strategy: 'fixed',
      skidding: this.skidding,
      onPositionChange: this.onPositionChange,
    };
  }

  private showPanel() {
    if (!this.panel) return;

    this.panel.classList.add('open');
    this.floatingUI?.update();

    this.autoDismissPanel();
  }

  private hidePanel() {
    if (!this.panel) return;

    this.open = false;
    this.panel.classList.remove('open');

    // Ensure that the timeout ID was definitely cleared.
    clearTimeout(this.timeoutId);
  }

  private togglePanel = async () => {
    this.open ? await this.hide() : await this.show();
  };

  private onTriggerClick = () => {
    if (this.disableTriggerClick) return;
    if (this.showOn !== 'click') return;

    /**
     * This is a "fire and forget" operation.
     * Details: https://stackoverflow.com/a/63488201
     */
    (async () => {
      await this.togglePanel();
    })();
  };

  private onBtnCloseClick = () => {
    /**
     * @See line #237
     */
    (async () => {
      await this.hide();
      // Focus the trigger element (if applicable)
      this.focusTriggerElement();
    })();
  };

  private onHeaderSlotChange = () => {
    if (!this.headerElement) return;

    this.hasHeader = hasSlotContent(this.headerElement);
  };

  private onPositionChange = (placement: FloatingUIPlacement) => {
    if (this.hideArrow) return;

    this.arrowElement.setAttribute('data-position', placement);
  };

  private onPanelTransitionStart = (event: TransitionEvent) => {
    if (!this.open) {
      this.pkHide.emit(event);
      return;
    }

    this.pkShow.emit(event);
  };

  private onPanelTransitionEnd = (event: TransitionEvent) => {
    if (!this.open) {
      this.pkAfterHide.emit(event);
      return;
    }

    this.pkAfterShow.emit(event);
  };

  private autoDismissPanel = () => {
    // Skip if `autodismiss` is disable (false) or `show-on` is set to hover
    if (!this.autodismiss || this.showOn === 'hover') return;

    this.timeoutId = setTimeout(() => {
      (async () => {
        await this.hide();
      })();
    }, this.dismissTime * 1000);
  };

  private focusTriggerElement = () => {
    const triggerElement = this.trigger
      .querySelector('slot')
      .assignedElements({ flatten: true })[0] as unknown as HTMLElement;
    if (!triggerElement) return;

    triggerElement.focus({
      preventScroll: true,
      focusVisible: true, // only works in Firefox
    } as FocusOptions);
  };

  private displayOverlay = () => {
    if (!this.enableOverlay || popoverOverlay.parentElement !== null) return;
    document.body.append(popoverOverlay);
  };

  private hideOverlay = () => {
    if (!this.enableOverlay) return;

    try {
      popoverOverlay.remove();
    } catch (error) {
      /**
       * Skip DOMException error since the popover overlay is optional,
       * meaning that it can be missing in the document.body.
       */
      if (error instanceof DOMException) return;
      throw error;
    }
  };

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    return (
      <div
        id={this.id}
        class="pk-popover relative"
        aria-controls={this.panelId}
        aria-haspopup="true"
        part="base"
        style={{
          '--popover-arrow--size': `${this.arrowSize}px`,
        }}
      >
        {/* TRIGGER */}
        <button
          type="button"
          onClick={this.onTriggerClick}
          class="pk-popover__trigger button-reset block"
          part="trigger"
          ref={(el) => (this.trigger = el)}
        >
          <slot name="trigger" />
        </button>
        {/* PANEL */}
        <div
          id={this.panelId}
          class={`pk-popover__panel ${this.size} ${this.stroke}`}
          aria-hidden={!this.open ? 'true' : 'false'}
          hidden={!this.open}
          part="panel"
          ref={(el) => (this.panel = el)}
        >
          {/* ARROW ELEMENT */}
          {!this.hideArrow && (
            <div
              class={{ 'pk-popover__arrow': true, stroke: this.stroke === 'stroke' }}
              part="arrow"
              ref={(el: HTMLElement) => (this.arrowElement = el)}
            />
          )}
          {/* CONTENT */}
          <div class="pk-popover__content flex flex-1 flex-col" part="content">
            {/* CONTENT HEADER */}
            {this.hasHeader && (
              <div
                class="pk-popover__header m-0 mb-m flex min-h-[32px] items-center p-0"
                part="header"
                ref={(header: HTMLDivElement) => (this.headerElement = header)}
              >
                <slot name="header" onSlotchange={this.onHeaderSlotChange} />
              </div>
            )}
            {/* CONTENT BODY */}
            <div class="pk-popover__body" part="body">
              <slot />
            </div>
          </div>
          {/* BUTTON CLOSE */}
          {!this.noCloseButton && (
            <pk-button
              class={{ '!hidden': !this.open }}
              aria-label="close"
              appearance="icon"
              part="close-button"
              type="button"
              onPkClick={this.onBtnCloseClick}
              soft
            >
              <pk-icon name="navigation-close" color="text-02" role="img" title="close" part="close-button-icon" />
            </pk-button>
          )}
        </div>
      </div>
    );
  }
}
